import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createUUId} from "../../../core/services/utils/utils";
import {
    buildFluidImageCarouselResponsive,
    buildImageCarouselResponsive,
    computeFluidImageCarouselItemsPerView,
    getFluidImageCarouselItemsPerView,
    getImageCarouselNavState,
    getImageCarouselSlideStyles,
    isImageCarouselScrollable,
    isImageCarouselScrollableByDimensions,
    usesImageCarouselDimensions,
} from "../../../core/services/utils/image-carousel-owl";

const CAROUSEL_SLIDE_SPEED = 250;

/**
 * Shared Owl carousel layout for image carousels.
 * - Sized mode (imageWidth set): fixed-width slides via autoWidth
 * - Fluid mode (no imageWidth): viewport-based items per row
 *
 * @param {{
 *   images: any[],
 *   imageWidth?: number | null,
 *   imageHeight?: number | null,
 *   margin?: number,
 *   viewportWidth?: string | null,
 *   reactKey?: boolean,
 * }} options
 */
export default function useImageCarouselOwl({
                                                images = [],
                                                imageWidth,
                                                imageHeight,
                                                margin,
                                                viewportWidth = null,
                                                reactKey = false,
                                            }) {
    const sized = usesImageCarouselDimensions(imageWidth);
    const [containerWidth, setContainerWidth] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(2);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    /**@type {React.MutableRefObject<import('react-owl-carousel').default>}*/
    const carouselRef = useRef();
    const sliderRef = useRef(null);
    const containerIdRef = useRef(createUUId(reactKey));

    const fluidItemsPerView = viewportWidth
        ? getFluidImageCarouselItemsPerView(viewportWidth)
        : itemsPerView;

    const isScrollable = sized
        ? isImageCarouselScrollableByDimensions(containerWidth, imageWidth, images.length, margin)
        : isImageCarouselScrollable(images.length, fluidItemsPerView);

    const responsive = useMemo(() => {
        if (sized) {
            return {0: {items: 1, margin}};
        }
        if (viewportWidth) {
            return buildFluidImageCarouselResponsive(viewportWidth, margin);
        }
        return buildImageCarouselResponsive(itemsPerView, margin);
    }, [sized, viewportWidth, itemsPerView, margin]);

    const {slideStyle, imageStyle} = useMemo(() => getImageCarouselSlideStyles(imageWidth, imageHeight), [imageWidth, imageHeight],);

    /**
     * Sets up event listeners to handle layout updates for the carousel.
     * It uses a ResizeObserver to monitor changes in the size of the slider element and updates the container width and items per view accordingly.
     * Additionally, it listens for window resize events to ensure the carousel layout remains responsive. The effect cleans up the event listeners when the component is unmounted or when dependencies change.
     */
    useEffect(() => {
        const sliderElement = sliderRef.current;
        if (!sliderElement) {
            return;
        }
        const updateLayout = () => {
            const width = sliderElement.clientWidth;
            setContainerWidth(width);
            if (!sized && !viewportWidth) {
                setItemsPerView(computeFluidImageCarouselItemsPerView(width, images.length, margin));
            }
        };
        updateLayout();
        const resizeObserver = new ResizeObserver(updateLayout);
        resizeObserver.observe(sliderElement);
        window.addEventListener('resize', updateLayout);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateLayout);
        };
    }, [sized, viewportWidth, images.length, margin]);

    /**
     * Triggers a window resize event after a delay when the images or layout-related properties change.
     * This is to ensure that the carousel recalculates its layout and navigation state correctly after changes that may affect the dimensions of the slides or the number of items per view.
     * The effect checks if there are images before setting up the timer, and it cleans up the timer when dependencies change or when the component is unmounted.
     */
    useEffect(() => {
        if (!images.length) {
            return;
        }
        const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
        return () => clearTimeout(timer);
    }, [images, sized, imageWidth, imageHeight, fluidItemsPerView, viewportWidth]);

    /**
     * Updates the navigation state of the carousel based on the event data. It checks if the event contains navigation state information and updates the hasNext and hasPrev states accordingly.
     * If the event does not contain navigation state but the carousel is scrollable, it defaults to setting hasNext to true and hasPrev to false.
     * This function is memoized using useCallback to prevent unnecessary re-renders when used as an event handler for carousel events.
     * @type {(function(*): void)|*}
     */
    const updateNavState = useCallback((event) => {
        const navState = getImageCarouselNavState(event);
        if (navState) {
            setHasNext(navState.hasNext);
            setHasPrev(navState.hasPrev);
            return;
        }
        if (isScrollable) {
            setHasNext(true);
            setHasPrev(false);
        }
    }, [isScrollable]);

    /**
     * Handles the slide change action for the carousel. It takes an offset parameter to determine the direction of the slide change (positive for next, negative for previous).
     * @type {(function(*): void)|*}
     */
    const changeSlide = useCallback((offset) => {
        if (!carouselRef.current) {
            return;
        }
        if (offset > 0) {
            carouselRef.current.next(CAROUSEL_SLIDE_SPEED);
            return;
        }
        carouselRef.current.prev(CAROUSEL_SLIDE_SPEED);
    }, []);

    /**
     * Handles the initialization event of the carousel. It calls the updateNavState function to set the initial navigation state based on the event data when the carousel is first initialized.
     * @type {(function(*): void)|*}
     */
    const onInitialized = useCallback((event) => {
        updateNavState(event);
    }, [updateNavState]);

    /**
     * Handles the change event of the carousel. It checks if the position property of the event has changed to determine if the user is dragging the carousel.
     * It then calls the updateNavState function to update the navigation state based on the new position of the carousel after a slide change occurs.
     * @type {(function(*): void)|*}
     */
    const onChanged = useCallback((event) => {
        updateNavState(event);
    }, [updateNavState]);

    const owlClassName = sized ? 'owl-image-carousel owl-image-carousel--sized' : 'owl-image-carousel';

    return {
        sliderRef,
        carouselRef,
        containerIdRef,
        sized,
        isScrollable,
        hasNext,
        hasPrev,
        slideStyle,
        imageStyle,
        responsive,
        owlClassName,
        owlProps: {
            autoplay: false,
            loop: false,
            margin,
            autoWidth: sized,
            touchDrag: true,
            mouseDrag: true,
            pullDrag: true,
            slideBy: 1,
            responsive,
        },
        changeSlide,
        onInitialized,
        onChanged,
    };
}
